<template>
  <div>
    <WizardModal
      modal-id="wizard-modal"
      :is-active.sync="isWizardActive"
      :is-add.sync="isAdd"
      :wizard-title="wizardTitle"
      :steps="wizardSteps"
      :step-icons="wizardStepIcons"
      :finishAtStep="finishAtStep"
      :current-step="currentStep"
      :show-previous-button="currentStep > 0"
      :show-save-button="!isAdd && currentStep < finishAtStep"
      :show-next-button="currentStep < finishAtStep"
      :show-finish-button="currentStep === finishAtStep"
      :activate-btn-save="activateBtnSave"
      :loading="loadingStep"
      @close-wizard="closeWizard"
      @prev-step="prevStep"
      @next-step="nextStep"
      @save-data="submit"
      @on-submit="submit"
      @saved="fetchData"
    >
      <template #step-0="{ currentStep, loading }">
        <GeneralInfoStep
          ref="generalInfoStepRef"
          :is-add.sync="isAdd"
          :prop-item-data="itemData"
          :master-account-options="masterAccountOptions"
          :type-alarms-options="typeAlarmsOptions"
          :tenant-options="tenantOptions"
          @update:item-data="itemData = { ...itemData, ...$event }"
        />
      </template>
      <template #step-1="{ currentStep, loading }">
        <RecognitionSettingsStep
          ref="recognitionConfigStepRef"
          :prop-item-data="itemData"
          @update:item-data="itemData = { ...itemData, ...$event }"
        />
      </template>
      <template #step-2="{ currentStep, loading }">
        <ScheduleDisable
          ref="scheduleSettingsStepRef"
          :prop-item-data="itemData"
          @update:item-data="itemData = { ...itemData, ...$event }"
        />
      </template>
    </WizardModal>
  </div>
</template>
<script>
import WizardModal from "@/components/WizardModal/Index.vue";
import { ref, computed, onMounted, watch, nextTick } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import axios from "@axios";
import GeneralInfoStep from "./GeneralInfoStep.vue";
import RecognitionSettingsStep from "./RecognitionSettingsStep.vue";
import ScheduleDisable from "./ScheduleDisable.vue";
import store from "@/store";

export default {
  components: {
    WizardModal,
    ToastificationContent,
    ToastificationLoadingContent,
    GeneralInfoStep,
    RecognitionSettingsStep,
    ScheduleDisable,
  },
  props: {
    isWizardActive: {
      type: Boolean,
      required: true,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    masterAccountOptions: {
      type: Array,
      required: true,
    },
    typeAlarmsOptions: {
      type: Array,
      required: true,
    },
    tenantOptions: {
      type: Array,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18nUtils();
    const toast = useToast();
    const trans = computed(() => ({}));

    const loadingStep = ref(false);

    const generalInfoStepRef = ref(null);
    const recognitionConfigStepRef = ref(null);
    const scheduleSettingsStepRef = ref(null);

    const wizardTitle = "Wizard Setup";

    const blankData = ref({
      id: null,
      name: "",
      comment: "",
      color: "",
      acknowledge: false,
      notify: false,
      master_account_id: "",
      alarm_type: "",
      face_threshold: 0.75,
      body_threshold: 0,
      car_threshold: 0,
      ignore_events: false,
      send_events_to_external_vms: false,
      tenant_id: null,
      id_master_account: null,
      is_blacklist: false,
      disable_schedule: {
        monday: false,
        monday_start: "00:00",
        monday_end: "00:00",
        tuesday: false,
        tuesday_start: "00:00",
        tuesday_end: "00:00",
        wednesday: false,
        wednesday_start: "00:00",
        wednesday_end: "00:00",
        thursday: false,
        thursday_start: "00:00",
        thursday_end: "00:00",
        friday: false,
        friday_start: "00:00",
        friday_end: "00:00",
        saturday: false,
        saturday_start: "00:00",
        saturday_end: "00:00",
        sunday: false,
        sunday_start: "00:00",
        sunday_end: "00:00",
      },
    });

    const days = [
      { label: "Monday", value: "monday" },
      { label: "Tuesday", value: "tuesday" },
      { label: "Wednesday", value: "wednesday" },
      { label: "Thursday", value: "thursday" },
      { label: "Friday", value: "friday" },
      { label: "Saturday", value: "saturday" },
      { label: "Sunday", value: "sunday" },
    ];

    function normalizeSchedule(rawSchedule) {
      const result = {};
      days.forEach((day) => {
        const val = rawSchedule?.[day.value];
        if (
          Array.isArray(val) &&
          val.length > 0 &&
          Array.isArray(val[0]) &&
          val[0].length === 2 &&
          typeof val[0][0] === "string" &&
          typeof val[0][1] === "string"
        ) {
          // Handles [["00:00:00", "01:00:00"]]
          result[day.value] = true;
          result[`${day.value}_start`] = val[0][0].slice(0, 5); // "hh:mm"
          result[`${day.value}_end`] = val[0][1].slice(0, 5); // "hh:mm"
        } else {
          result[day.value] = false;
          result[`${day.value}_start`] = "00:00";
          result[`${day.value}_end`] = "00:00";
        }
      });
      return result;
    }

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));

    const wizardSteps = computed(() => [
      t("ntechlab.watch_list.info"),
      t("ntechlab.watch_list.reconogition_config"),
      t("ntechlab.watch_list.schedule_settings"),
    ]);

    const wizardStepIcons = ["ToolIcon", "AlertOctagonIcon", "ClockIcon"];

    const finishAtStep = ref(2);
    const currentStep = ref(0);
    const isUpdate = ref(false);
    const firstItemDataOnEdit = ref(null);
    const isInitialLoadComplete = ref(false);
    const disableBtnSave = ref(false);
    const stepRefs = ref([
      generalInfoStepRef,
      recognitionConfigStepRef,
      scheduleSettingsStepRef,
    ]);

    const activateBtnSave = ref(false);

    const closeWizard = () => {
      resetData();
      isUpdate.value = false;
      activateBtnSave.value = false;
      emit("update:isWizardActive", false);
    };

    const prevStep = () => {
      currentStep.value -= 1;
    };

    const validateCurrentStep = async () => {
      await nextTick();

      const currentRef = stepRefs.value[currentStep.value]?.value;

      if (currentRef && typeof currentRef.validateStep === "function") {
        const validation = await currentRef.validateStep();
        return validation;
      }
      return true;
    };

    const nextStep = async () => {
      try {
        const isValid = await validateCurrentStep();

        if (!isValid) {
          return; // Stop here if validation fails
        }

        loadingStep.value = true;

        if (currentStep.value >= 1) {
          const saveSuccessful = await saveData(false);
          // if (!saveSuccessful) {
          //   return;
          // }
        }
        currentStep.value++;
      } catch (error) {
        console.error("Error during step validation or save:", error);
      } finally {
        loadingStep.value = false;
      }
    };

    const handleSave = async () => {
      await saveData(false);
    };

    const onSubmit = async () => {
      const saveSuccessful = await saveData(true);

      if (!saveSuccessful) {
        return false;
      }
    };

    watch(
      () => itemData.value,
      (newVal, oldVal) => {
        if (props.isAdd) {
          activateBtnSave.value = false;
          return;
        }

        if (!isInitialLoadComplete.value || !firstItemDataOnEdit.value) {
          return;
        }

        const currentData = JSON.stringify(newVal);
        const originalData = firstItemDataOnEdit.value;

        activateBtnSave.value = currentData !== originalData;
      },
      { deep: true, immediate: false }
    );

    const loadData = () => {
      if (props.isAdd) {
        itemData.value = JSON.parse(JSON.stringify(blankData.value || blankData));
      } else {
        // Merge blank and edit, but normalize disable_schedule from backend
        const merged = {
          ...JSON.parse(JSON.stringify(blankData.value || blankData)),
          ...props.itemEdit,
        };
        merged.disable_schedule = normalizeSchedule(
          props.itemEdit?.disable_schedule || {}
        );
        itemData.value = merged;
      }
      currentStep.value = 0;
      isUpdate.value = false;
      activateBtnSave.value = false;

      // Initialize the first data state for comparison
      setTimeout(() => {
        firstItemDataOnEdit.value = JSON.stringify(itemData.value);
        isInitialLoadComplete.value = true;
      }, 100);
    };

    const formatTimeToHHMM = (time) => {
      if (!time) return "00:00";
      if (/^\d{2}:\d{2}$/.test(time)) return time;
      if (/^\d{2}:\d{2}:\d{2}$/.test(time)) return time.slice(0, 5);
      return "00:00";
    };

    const submit = async () => {
      console.log("submit called in Wizard.vue");
      let response;
      loadingStep.value = true;

      toast({
        component: ToastificationContent,
        props: {
          title: "Saving Watchlist",
          icon: "InfoIcon",
          variant: "info",
        },
      });

      try {
        if (props.isAdd) {
          itemData.value.disable_schedule = {
            monday: itemData.value.disable_schedule.monday
              ? [
                  formatTimeToHHMM(itemData.value.disable_schedule.monday_start),
                  formatTimeToHHMM(itemData.value.disable_schedule.monday_end),
                ]
              : null,
            tuesday: itemData.value.disable_schedule.tuesday
              ? [
                  formatTimeToHHMM(itemData.value.disable_schedule.tuesday_start),
                  formatTimeToHHMM(itemData.value.disable_schedule.tuesday_end),
                ]
              : null,
            wednesday: itemData.value.disable_schedule.wednesday
              ? [
                  formatTimeToHHMM(itemData.value.disable_schedule.wednesday_start),
                  formatTimeToHHMM(itemData.value.disable_schedule.wednesday_end),
                ]
              : null,
            thursday: itemData.value.disable_schedule.thursday
              ? [
                  formatTimeToHHMM(itemData.value.disable_schedule.thursday_start),
                  formatTimeToHHMM(itemData.value.disable_schedule.thursday_end),
                ]
              : null,
            friday: itemData.value.disable_schedule.friday
              ? [
                  formatTimeToHHMM(itemData.value.disable_schedule.friday_start),
                  formatTimeToHHMM(itemData.value.disable_schedule.friday_end),
                ]
              : null,
            saturday: itemData.value.disable_schedule.saturday
              ? [
                  formatTimeToHHMM(itemData.value.disable_schedule.saturday_start),
                  formatTimeToHHMM(itemData.value.disable_schedule.saturday_end),
                ]
              : null,
            sunday: itemData.value.disable_schedule.sunday
              ? [
                  formatTimeToHHMM(itemData.value.disable_schedule.sunday_start),
                  formatTimeToHHMM(itemData.value.disable_schedule.sunday_end),
                ]
              : null,
          };
          response = await store.dispatch("ntechlab_watch_list/add", itemData.value);
          if (response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: props.isAdd
                  ? "Watchlist Saved Successfully"
                  : "Watchlist Updated Successfully",
                icon: "CheckIcon",
                variant: "success",
              },
            });

            if (!props.isAdd) {
              firstItemDataOnEdit.value = JSON.stringify(itemData.value);
              activateBtnSave.value = false;
            }

            closeWizard();
            emit("saved");
          }
        } else {
          const req = {
            params: {
              id: itemData.value.id,
              name: itemData.value.name,
              comment: itemData.value.comment,
              color: itemData.value.color,
              master_account_id: itemData.value.master_account_id,
              alarm_type: itemData.value.alarm_type,
              face_threshold: itemData.value.face_threshold,
              body_threshold: itemData.value.body_threshold,
              car_threshold: itemData.value.car_threshold,
              ignore_events: itemData.value.ignore_events,
              send_events_to_external_vms: itemData.value.send_events_to_external_vms,
              tenant_id: itemData.value.tenant_id,
              alarm_type: itemData.value.alarm_type,
              id_master_account: itemData.value.id_master_account,
              acknowledge: itemData.value.acknowledge,
              notify: itemData.value.notify,
              send_events_to_external_vms: itemData.value.send_events_to_external_vms,
              ignore_events: itemData.value.ignore_events,
              is_blacklist: itemData.value.is_blacklist,
              disable_schedule: {
                monday: itemData.value.disable_schedule.monday
                  ? [
                      formatTimeToHHMM(itemData.value.disable_schedule.monday_start),
                      formatTimeToHHMM(itemData.value.disable_schedule.monday_end),
                    ]
                  : null,
                tuesday: itemData.value.disable_schedule.tuesday
                  ? [
                      formatTimeToHHMM(itemData.value.disable_schedule.tuesday_start),
                      formatTimeToHHMM(itemData.value.disable_schedule.tuesday_end),
                    ]
                  : null,
                wednesday: itemData.value.disable_schedule.wednesday
                  ? [
                      formatTimeToHHMM(itemData.value.disable_schedule.wednesday_start),
                      formatTimeToHHMM(itemData.value.disable_schedule.wednesday_end),
                    ]
                  : null,
                thursday: itemData.value.disable_schedule.thursday
                  ? [
                      formatTimeToHHMM(itemData.value.disable_schedule.thursday_start),
                      formatTimeToHHMM(itemData.value.disable_schedule.thursday_end),
                    ]
                  : null,
                friday: itemData.value.disable_schedule.friday
                  ? [
                      formatTimeToHHMM(itemData.value.disable_schedule.friday_start),
                      formatTimeToHHMM(itemData.value.disable_schedule.friday_end),
                    ]
                  : null,
                saturday: itemData.value.disable_schedule.saturday
                  ? [
                      formatTimeToHHMM(itemData.value.disable_schedule.saturday_start),
                      formatTimeToHHMM(itemData.value.disable_schedule.saturday_end),
                    ]
                  : null,
                sunday: itemData.value.disable_schedule.sunday
                  ? [
                      formatTimeToHHMM(itemData.value.disable_schedule.sunday_start),
                      formatTimeToHHMM(itemData.value.disable_schedule.sunday_end),
                    ]
                  : null,
              },
            },
          };
          const idWatchList = itemData.value.id;
          response = await store.dispatch("ntechlab_watch_list/edit", {
            id: idWatchList,
            params: req.params,
          });
          if (response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: props.isAdd
                  ? "Watchlist Saved Successfully"
                  : "Watchlist Updated Successfully",
                icon: "CheckIcon",
                variant: "success",
              },
            });
            if (!props.isAdd) {
              firstItemDataOnEdit.value = JSON.stringify(itemData.value);
              activateBtnSave.value = false;
            }
            closeWizard();
            emit("saved");
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: "Error Updating Watchlist",
                icon: "XIcon",
                variant: "error",
              },
            });
          }
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error Saving Watchlist",
            icon: "XIcon",
            variant: "error",
          },
        });
      }
    };

    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData.value || blankData));
      currentStep.value = 0;
      isUpdate.value = false;
      activateBtnSave.value = false;
      isInitialLoadComplete.value = false;
      firstItemDataOnEdit.value = null;
    };

    const saveData = async (shouldClose = false) => {
      loadingStep.value = true;
      try {
      } catch (error) {}
    };

    const onShown = () => {
      loadData();
    };

    watch(
      () => props.isWizardActive,
      (newVal) => {
        if (newVal) {
          loadData();
        } else {
          resetData();
        }
      }
    );

    const fetchData = () => {
      // Your logic to reload the watchlist data
      // For example:
      loadData(); // or whatever your data loading method is called
    };

    return {
      itemData,
      wizardTitle,
      wizardSteps,
      wizardStepIcons,
      finishAtStep,
      currentStep,
      closeWizard,
      prevStep,
      nextStep,
      handleSave,
      loadData,
      resetData,
      onSubmit,
      isUpdate,
      loadingStep,
      activateBtnSave,
      generalInfoStepRef,
      onShown,
      submit,
      fetchData,
      disableBtnSave,
    };
  },
};
</script>
